import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
import { installAuthRefresh } from "./lib/auth-refresh";
import "./index.css";

function RouteSuspenseFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-neutral-600">
      <span className="inline-flex items-center gap-3">
        <span className="size-2 rounded-full bg-accent-500 animate-pulse" />
        Loading…
      </span>
    </div>
  );
}

// Patch window.fetch so 401s from /api/* auto-trigger /api/auth/refresh
// and replay the request. Must run before the router mounts.
installAuthRefresh();

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<RouteSuspenseFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </QueryClientProvider>
  </React.StrictMode>,
);

// A lazy route chunk can fail to load when the app was opened before a
// deploy (old index.html → chunk name that no longer exists). Vite raises
// `vite:preloadError` for that; reload once so the new index.html and its
// chunks are picked up instead of leaving a blank page.
window.addEventListener("vite:preloadError", (event) => {
  const key = "ailogistics:chunk-reload";
  if (sessionStorage.getItem(key)) return; // never loop
  sessionStorage.setItem(key, "1");
  event.preventDefault();
  window.location.reload();
});
window.addEventListener("load", () => sessionStorage.removeItem("ailogistics:chunk-reload"));

// PWA service worker — production only. In dev we let Vite serve fresh
// modules without an intermediary cache layer.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Swallow registration failures (private mode, HTTP origin, etc).
    });
  });
}
